import type { ProfessionCatalogItemDto } from "@/services/UserIdentitiesService";
import type { CompanyAddressDto, CompanyDetailsDraftDto, CompanyDocumentDto, LegalRepresentativeRole, LegalRepresentativeSign, SaveCompanyDetailsRequest, SelectableRepresentativeDto } from "@/services/CompanyOnboardingService";

export interface CatalogOption
{
    value: string;
    text: string;
}

export interface CompanyAddressFormData
{
    country: string;
    zipCode: string;
    city: string;
    roadType: string;
    streetName: string;
    streetNumber: string;
    streetFloor: string;
    streetDoor: string;
    streetOther: string;
}

export interface CompanyRepresentativeFormRow
{
    userIdentityId: number;
    fullName: string;
    maskedIdDocument: string;
    role: LegalRepresentativeRole | "";
    sign: LegalRepresentativeSign | "";
    ownership: string;
}

export interface CompanyDetailsFormData
{
    taxId: string;
    // Información básica
    businessName: string;
    sector: string;
    cnae: string;
    incorporationDate: string;
    incorporationCountry: string;
    incorporationPlace: string;
    nationalCountry: string;
    numberOfEmployees: string;
    lei: string;
    leiExpirationDate: string;
    numCnmv: string;
    sicav: string;
    digitalSignature: "" | "yes" | "no";
    // Registro mercantil
    commercialRegistry: string;
    registryProvince: string;
    volume: string;
    page: string;
    sheet: string;
    protocol: string;
    notary: string;
    // Domicilios
    fiscalAddress: CompanyAddressFormData;
    registeredAddressSameAsFiscal: boolean;
    registeredAddress: CompanyAddressFormData;
    // Contacto
    email: string;
    countryPrefix: string;
    phone: string;
    representatives: CompanyRepresentativeFormRow[];
}

export type CompanyDetailsTextField = Exclude<keyof CompanyDetailsFormData, "fiscalAddress" | "registeredAddress" | "registeredAddressSameAsFiscal" | "representatives" | "digitalSignature">;

export const SpainCountryCode = "ES";
export const BeneficialOwnerRole: LegalRepresentativeRole = "TitularReal";

// Mismo catálogo de sectores que el backoffice de Omnia (los valores son los que guarda Omnia)
export const SectorOptions: CatalogOption[] =
[
    { value: "MULTISECTORIAL", text: "Multisectorial" },
    { value: "ENERGÍA", text: "Energía" },
    { value: "MATERIALES", text: "Materiales" },
    { value: "INDUSTRIAL", text: "Industrial" },
    { value: "CONSUMO DISCRECIONAL", text: "Consumo discrecional" },
    { value: "PDTOS 1ª NECESIDAD", text: "Productos de 1ª necesidad" },
    { value: "SALUD", text: "Salud" },
    { value: "FINANZAS", text: "Finanzas" },
    { value: "TECNOLOGÍA", text: "Tecnología" },
    { value: "COMUNICACIÓN", text: "Comunicación" },
    { value: "SERVICIOS PÚBLICOS", text: "Servicios públicos" },
    { value: "INMOBILIARIO", text: "Inmobiliario" },
    { value: "OTROS", text: "Otros" },
];

export const RoleOptions: { value: LegalRepresentativeRole; text: string }[] =
[
    { value: "TitularReal", text: "Titular real" },
    { value: "ApoderadoLegal", text: "Apoderado legal" },
];

export const SignOptions: { value: LegalRepresentativeSign; text: string }[] =
[
    { value: "NoSignature", text: "Sin firma" },
    { value: "Solidary", text: "Firma solidaria" },
    { value: "Joint", text: "Firma mancomunada" },
];

// Campos con asterisco de la maqueta (los domicilios, documentos y representantes se revisan aparte)
export const RequiredCompanyFields: CompanyDetailsTextField[] =
[
    "businessName", "sector", "cnae", "incorporationDate", "incorporationCountry", "nationalCountry",
    "commercialRegistry", "registryProvince", "email", "countryPrefix", "phone",
];

const RequiredAddressFields: (keyof CompanyAddressFormData)[] = ["country", "zipCode", "city", "roadType", "streetName", "streetNumber"];

const EmailPattern = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export const getEmptyAddress = (): CompanyAddressFormData =>
({
    country: SpainCountryCode,
    zipCode: "",
    city: "",
    roadType: "",
    streetName: "",
    streetNumber: "",
    streetFloor: "",
    streetDoor: "",
    streetOther: "",
});

const text = (value: string | number | null | undefined): string => (value === null || value === undefined ? "" : String(value));
const dateOnly = (value: string | null | undefined): string => (value ? value.substring(0, 10) : "");

const toAddressForm = (address: CompanyAddressDto | null | undefined): CompanyAddressFormData =>
{
    if(!address)
    {
        return getEmptyAddress();
    }
    return{
        country: address.country || SpainCountryCode,
        zipCode: text(address.zipCode),
        city: text(address.city),
        roadType: text(address.roadType),
        streetName: text(address.streetName),
        streetNumber: text(address.streetNumber),
        streetFloor: text(address.streetFloor),
        streetDoor: text(address.streetDoor),
        streetOther: text(address.streetOther),
    };
};

/** Formulario a partir del borrador guardado (paso 2 y lo que haya del paso 3). */
export function toCompanyDetailsFormData(draft: CompanyDetailsDraftDto): CompanyDetailsFormData
{
    return{
        taxId: text(draft.taxId),
        businessName: text(draft.businessName),
        sector: text(draft.sector),
        cnae: text(draft.cnae),
        incorporationDate: dateOnly(draft.incorporationDate),
        incorporationCountry: draft.incorporationCountry || SpainCountryCode,
        incorporationPlace: text(draft.incorporationPlace),
        nationalCountry: draft.nationalCountry || SpainCountryCode,
        numberOfEmployees: text(draft.numberOfEmployees),
        lei: text(draft.lei),
        leiExpirationDate: dateOnly(draft.leiExpirationDate),
        numCnmv: text(draft.numCnmv),
        sicav: text(draft.sicav),
        digitalSignature: draft.digitalSignature === true ? "yes" : draft.digitalSignature === false ? "no" : "",
        commercialRegistry: text(draft.commercialRegistry),
        registryProvince: text(draft.registryProvince),
        volume: text(draft.volume),
        page: text(draft.page),
        sheet: text(draft.sheet),
        protocol: text(draft.protocol),
        notary: text(draft.notary),
        fiscalAddress: toAddressForm(draft.fiscalAddress),
        registeredAddressSameAsFiscal: draft.registeredAddressSameAsFiscal,
        registeredAddress: toAddressForm(draft.registeredAddress),
        email: text(draft.email),
        countryPrefix: text(draft.countryPrefix),
        phone: text(draft.phone),
        representatives: (draft.legalRepresentatives ?? []).map((representative) =>
        ({
            userIdentityId: representative.userIdentityId,
            fullName: text(representative.fullName),
            maskedIdDocument: text(representative.maskedIdDocument),
            role: representative.role ?? "",
            sign: representative.sign ?? "",
            ownership: text(representative.ownership),
        })),
    };
}

/** Fila nueva de representante al elegir un interviniente: sin rol ni firma, para que se indiquen. */
export function toRepresentativeRow(selectable: SelectableRepresentativeDto): CompanyRepresentativeFormRow
{
    return{
        userIdentityId: selectable.userIdentityId,
        fullName: selectable.fullName,
        maskedIdDocument: text(selectable.maskedIdDocument),
        role: "",
        sign: "",
        ownership: "",
    };
}

const emptyToNull = (value: string): string | null => (value.trim() === "" ? null : value.trim());

const toNumberOrNull = (value: string): number | null =>
{
    const trimmed = value.trim().replace(",", ".");
    if(trimmed === "")
    {
        return null;
    }
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
};

const toIntegerOrNull = (value: string): number | null =>
{
    const parsed = toNumberOrNull(value);
    return parsed !== null && Number.isInteger(parsed) ? parsed : null;
};

const toAddressRequest = (address: CompanyAddressFormData): CompanyAddressDto =>
({
    country: emptyToNull(address.country),
    zipCode: emptyToNull(address.zipCode),
    city: emptyToNull(address.city),
    roadType: emptyToNull(address.roadType),
    streetName: emptyToNull(address.streetName),
    streetNumber: emptyToNull(address.streetNumber),
    streetFloor: emptyToNull(address.streetFloor),
    streetDoor: emptyToNull(address.streetDoor),
    streetOther: emptyToNull(address.streetOther),
});

/** Petición del guardado (SaveCompanyDetailsDto). complete = true para continuar al KYC. */
export function buildSaveCompanyDetailsRequest(data: CompanyDetailsFormData, complete: boolean): SaveCompanyDetailsRequest
{
    return{
        businessName: emptyToNull(data.businessName),
        sector: emptyToNull(data.sector),
        cnae: emptyToNull(data.cnae),
        incorporationDate: emptyToNull(data.incorporationDate),
        incorporationCountry: emptyToNull(data.incorporationCountry),
        incorporationPlace: emptyToNull(data.incorporationPlace),
        nationalCountry: emptyToNull(data.nationalCountry),
        numberOfEmployees: emptyToNull(data.numberOfEmployees),
        lei: emptyToNull(data.lei),
        leiExpirationDate: emptyToNull(data.leiExpirationDate),
        numCnmv: emptyToNull(data.numCnmv),
        sicav: emptyToNull(data.sicav),
        digitalSignature: data.digitalSignature === "" ? null : data.digitalSignature === "yes",
        commercialRegistry: emptyToNull(data.commercialRegistry),
        registryProvince: emptyToNull(data.registryProvince),
        volume: toIntegerOrNull(data.volume),
        page: toIntegerOrNull(data.page),
        sheet: emptyToNull(data.sheet),
        protocol: emptyToNull(data.protocol),
        notary: emptyToNull(data.notary),
        fiscalAddress: toAddressRequest(data.fiscalAddress),
        registeredAddressSameAsFiscal: data.registeredAddressSameAsFiscal,
        // Igual al fiscal: el backend lo copia
        registeredAddress: data.registeredAddressSameAsFiscal ? null : toAddressRequest(data.registeredAddress),
        email: emptyToNull(data.email),
        countryPrefix: emptyToNull(data.countryPrefix),
        phone: emptyToNull(data.phone.replace(/\s/g, "")),
        legalRepresentatives: data.representatives.map((row) =>
        ({
            userIdentityId: row.userIdentityId,
            role: row.role === "" ? null : row.role,
            sign: row.sign === "" ? null : row.sign,
            // El porcentaje solo cuenta para el titular real
            ownership: row.role === BeneficialOwnerRole ? toNumberOrNull(row.ownership) : null,
        })),
        complete,
    };
}

export function isAddressComplete(address: CompanyAddressFormData): boolean
{
    return RequiredAddressFields.every((field) => address[field].trim() !== "");
}

/** Campos obligatorios vacíos o con formato no válido (para marcarlos al continuar). */
export function getMissingCompanyFields(data: CompanyDetailsFormData): CompanyDetailsTextField[]
{
    const missing = RequiredCompanyFields.filter((field) => data[field].trim() === "");
    if(data.email.trim() !== "" && !EmailPattern.test(data.email.trim()))
    {
        missing.push("email");
    }
    if(data.incorporationDate !== "" && data.incorporationDate > new Date().toISOString().substring(0, 10))
    {
        missing.push("incorporationDate");
    }
    return missing;
}

/** Problemas de los representantes, con el mismo criterio que el backend. Vacío si está todo bien. */
export function getRepresentativeIssues(rows: CompanyRepresentativeFormRow[]): string[]
{
    const issues: string[] = [];
    if(rows.length === 0)
    {
        return ["Selecciona al menos un representante."];
    }
    if(rows.some((row) => row.role === "" || row.sign === ""))
    {
        issues.push("Todos los representantes deben tener rol y firma asignados.");
    }

    const owners = rows.filter((row) => row.role === BeneficialOwnerRole);
    if(owners.length === 0)
    {
        issues.push("Al menos uno de los representantes debe ser titular real.");
    }
    const ownerships = owners.map((row) => toNumberOrNull(row.ownership));
    if(ownerships.some((value) => value === null))
    {
        issues.push("Indica el porcentaje de titularidad de cada titular real.");
    }
    if(ownerships.some((value) => value !== null && (value <= 0 || value > 100)))
    {
        issues.push("El porcentaje de titularidad debe ser mayor que 0 y como máximo 100.");
    }
    else if(ownerships.reduce<number>((total, value) => total + (value ?? 0), 0) > 100)
    {
        issues.push("La suma de los porcentajes de titularidad no puede superar el 100 %.");
    }
    return issues;
}

/** Documentos que faltan para continuar: al menos uno de constitución y el CIF. */
export function getMissingDocumentMessages(documents: CompanyDocumentDto[]): string[]
{
    const messages: string[] = [];
    if(!documents.some((document) => document.category === "Incorporation"))
    {
        messages.push("Adjunta al menos un documento de constitución.");
    }
    if(!documents.some((document) => document.category === "TaxIdCard"))
    {
        messages.push("Adjunta el documento CIF.");
    }
    return messages;
}

/** Opción del selector de CNAE (catálogo de Omnia): "G4689 - Otro comercio al por mayor...". */
export interface CnaeOption
{
    value: string;
    label: string;
}

export function toCnaeOptions(items: ProfessionCatalogItemDto[] | null | undefined): CnaeOption[]
{
    return (items ?? [])
        .filter((item) => !!item.code)
        .map((item) => ({ value: item.code, label: `${item.code} - ${item.description}` }))
        .sort((a, b) => a.value.localeCompare(b.value));
}

// Omnia solo admite documentos de empresa en PDF
export const AllowedDocumentExtensions = ["pdf"];
export const MaxDocumentBytes = 10 * 1024 * 1024;

/** Mensaje si el fichero no se puede subir (formato o tamaño); null si vale. */
export function getDocumentFileError(file: File): string | null
{
    const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
    if(!AllowedDocumentExtensions.includes(extension))
    {
        return "El documento debe ser un PDF.";
    }
    return file.size > MaxDocumentBytes ? "El documento no puede superar los 10 MB." : null;
}
